import { defineStore } from "pinia";
import useJwt from "@/auth/jwt/useJwt";
import Repository from "@/repositories/RepositoryFactory";
import { CallClient } from "@azure/communication-calling";
import { AzureCommunicationTokenCredential } from "@azure/communication-common";

const CallState = {
  INCOMING: "incoming",
  STARTED: "started",
  DECLINED: "declined",
  ENDED: "ended",
};

const CallRepository = Repository.get("call");

const useCallStore = defineStore("call", {
  state: () => ({
    call: null,
    incomingCall: null,
    callerInfo: null,
    callAgent: null,
    deviceManager: null,
    tokenCredential: null,
    shownIncomingCallModel: false,
    isMuted: false,
    isSpeaker: false,
    callState: null,
    shownCallRecordListModal: false,
    selectedCallRecord: null,
    acsCalleeUserId: null,
    acsCallerUserId: null,
    session: null,
    isMinimized: false,
    timer: null,
    isEndedCall: false,
    ringtone: null,
    selectedLanguage: null,
    speakers: [],
    defaultSpeaker: null,
    showSpeakerPopover: false,
    speakerSelectedId: "",
    isReleased: true, // for end
    isReleasedAccept: true, // for accept
  }),
  getters: {},
  actions: {
    async initCallClient() {
      if (!useJwt.getToken()) return;

      // Init ringtone
      this.ringtone = new Audio("default_ringing.mp3");
      this.ringtone.loop = true;

      const { data } = await CallRepository.createCallUser();
      const { acsToken, acsUserId } = data?.data || {};
      this.acsCalleeUserId = acsUserId;

      const callClient = new CallClient();

      try {
        this.tokenCredential = new AzureCommunicationTokenCredential(acsToken);
        this.callAgent = await callClient.createCallAgent(this.tokenCredential);

        this.deviceManager = await callClient.getDeviceManager();
        await this.deviceManager.askDevicePermission({
          audio: true,
          speaker: true,
        });

        this.callAgent.on("incomingCall", async (args) => {
          this.ringtone.play();
          this.isEndedCall = false;

          try {
            this.incomingCall = args.incomingCall;
            this.acsCallerUserId =
              args.incomingCall.callerInfo.identifier.communicationUserId;

            // Reset timer on minimized modal
            this.timer = null;

            // Get session
            this.session = await this.getSession();
            if (this.session) {
              this.selectedLanguage = this.session.language;
            }

            // Emit incoming event
            await this.emitNotify({
              session: this.session,
              callState: CallState.INCOMING,
            });

            // Incoming call ended
            this.incomingCall.on("callEnded", (e) => {
              this.isReleased = true;
              this.isReleasedAccept = true;

              // Not yet accepted
              if (!this.timer) {
                this.shownIncomingCallModel = false;
                this.isMinimized = false;
                this.ringtone.pause();
                this.timer = null;
              }

              this.isMuted = false;
              this.isSpeaker = false;
              this.defaultSpeaker = null;
              this.showSpeakerPopover = false;
              this.speakerSelectedId = "";
            });

            const { data: user } = await CallRepository.getUserById(
              this.acsCallerUserId
            );
            const { callUser } = user?.data || {};

            this.callerInfo = callUser;
            this.shownIncomingCallModel = true;
          } catch (error) {
            console.log(error);
          }
        });
      } catch (error) {
        console.log(error);
        throw new Error("Please submit a valid token!");
      }
    },
    async acceptCall(onStartCalling) {
      // eslint-disable-next-line no-underscore-dangle
      if (this.incomingCall._call._state === "Connected") return;

      // this.isReleased = false;
      this.isReleasedAccept = false;

      try {
        this.ringtone.pause();
        this.call = await this.incomingCall.accept();

        this.call.on("stateChanged", async () => {
          if (this.call.state === "Connected") {
            this.isReleased = true;
            await this.emitNotify({
              session: this.session,
              callState: CallState.STARTED,
            });
            onStartCalling();
          } else if (this.call.state === "Disconnected") {
            // Accepted the call
            this.isEndedCall = true;
            this.isMinimized = false;
            this.ringtone.pause();
            this.timer = null;

            setTimeout(() => {
              this.shownIncomingCallModel = false;
            }, 2000);
          }
        });
      } catch (error) {
        console.log(error);
      }
    },
    async endCall(counter, onEndCalling) {
      this.isReleased = false;

      try {
        if (counter > 0 || this.timer) {
          await this.emitNotify({
            session: this.session,
            callState: CallState.ENDED,
          });
          this.isEndedCall = true;
          this.call.hangUp({ forEveryone: true });
        } else {
          await this.emitNotify({
            session: this.session,
            callState: CallState.DECLINED,
          });
          this.incomingCall.reject();
        }

        if (onEndCalling) {
          onEndCalling();
        }
      } catch (error) {
        this.shownIncomingCallModel = false;
        console.log(error);
      }
    },
    toggleIncomingCallModel() {
      this.shownIncomingCallModel = !this.shownIncomingCallModel;
    },
    async toggleIsMuted() {
      if (this.isMuted) {
        await this.call.unmute();
      } else {
        await this.call.mute();
      }

      this.isMuted = !this.isMuted;
    },
    async toggleIsSpeaker() {
      // Get speakers
      if (this.deviceManager) {
        this.speakers = await this.deviceManager.getSpeakers();

        this.defaultSpeaker = this.speakers.find(
          (speaker) => speaker.isSystemDefault
        );
      }

      if (this.deviceManager.isSpeakerSelectionAvailable) {
        this.showSpeakerPopover = !this.showSpeakerPopover;
      }
    },
    toggleSpeakerOnOff(selectedSpeaker) {
      // Unset the current speaker
      if (this.speakerSelectedId === selectedSpeaker.id) {
        this.deviceManager.selectSpeaker(this.defaultSpeaker);
        this.speakerSelectedId = "";
        this.isSpeaker = false;
        this.showSpeakerPopover = false;
        return;
      }

      this.deviceManager.selectSpeaker(selectedSpeaker);
      this.speakerSelectedId = selectedSpeaker.id;
      this.isSpeaker = true;
      this.showSpeakerPopover = false;
    },
    toggleMinimize() {
      this.shownIncomingCallModel = !this.shownIncomingCallModel;
      this.isMinimized = !this.isMinimized;
    },
    showCallRecordListModal(item) {
      this.shownCallRecordListModal = true;
      this.selectedCallRecord = item;
    },
    async getSession() {
      try {
        const { data } = await CallRepository.findSession({
          acsCallerUserId: this.acsCallerUserId,
          acsCalleeUserId: this.acsCalleeUserId,
        });

        const { session } = data?.data || {};

        return session;
      } catch (error) {
        console.log(error);
      }

      return null;
    },
    async emitNotify({ session, callState }) {
      try {
        if (!session) {
          return;
        }

        await CallRepository.notify({
          sessionId: session.id,
          type: callState,
        });
      } catch (error) {
        console.log(error);
      }
    },
    disposeAgent() {
      if (this.callAgent) {
        this.callAgent.dispose();
        this.callAgent = null;
      }
    },
  },
});

export default useCallStore;
